import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, CheckCircle2, RefreshCw } from "lucide-react";
import { forgotPasswordApi, AuthApiError } from "@/services/auth.service";

export function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Resend verification
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setIsLoading(true);

    try {
      await forgotPasswordApi(email);
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          setError(err.message);
        }
      } else {
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setIsResending(true);
    setResendMessage(null);

    try {
      await forgotPasswordApi(email);
      setResendMessage("Email de réinitialisation renvoyé avec succès.");
    } catch {
      setResendMessage("Impossible de renvoyer l'email.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 font-sans">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100">
        {isSuccess ? (
          <div className="text-center space-y-5">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-black text-[#003366]">Email envoyé !</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              Si un compte est associé à <span className="font-bold text-gray-700">{email}</span>,
              vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
            </p>
            <p className="text-xs text-gray-400">
              Vérifiez votre dossier spam si vous ne voyez pas l'email.
            </p>

            {/* Resend verification email */}
            <div className="border-t border-gray-100 pt-4">
              {resendMessage && (
                <p className="text-sm font-bold text-emerald-600 mb-3">{resendMessage}</p>
              )}
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="text-indigo-600 text-xs font-black uppercase tracking-widest hover:underline inline-flex items-center gap-2 disabled:opacity-50"
              >
                {isResending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RefreshCw size={14} />
                )}
                Renvoyer le mail
              </button>
            </div>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full bg-[#003366] text-white rounded-xl py-4 font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all"
            >
              Retour à la connexion
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black text-[#003366] mb-2">Mot de passe oublié</h1>
            <p className="text-gray-400 text-sm mb-8">
              Entrez votre email pour recevoir un lien de réinitialisation.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Adresse Email
                </label>
                <div className="relative group">
                  <Mail
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors"
                    size={18}
                  />
                  <input
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-12 pr-4 py-3.5 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 focus:bg-white transition-all text-gray-900 font-semibold disabled:opacity-50"
                    required
                  />
                </div>
                {fieldErrors.email && (
                  <p className="text-sm text-red-500 font-semibold">{fieldErrors.email[0]}</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm font-bold text-red-600">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#003366] text-white rounded-xl py-4 font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  "Envoyer le lien"
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full text-indigo-600 font-black text-[11px] uppercase tracking-widest hover:underline inline-flex items-center justify-center gap-2 py-2"
              >
                <ArrowLeft size={14} />
                Retour à la connexion
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
